import React, { useState } from 'react';
import {
	Calendar,
	Edit3,
	Check,
	X,
	CheckSquare,
	Clock,
	MessageCircle,
} from 'lucide-react';
import Card from '../../../../components/ui/card';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';

const SchedulingCard = ({ client, clientDataHook }) => {
	const { updateField, isEditMode, toggleEditMode, cancelEdit } =
		clientDataHook;
	const [editValues, setEditValues] = useState({});

	const fields = [
		{
			key: 'faqAcknowledged',
			label: 'FAQ Acknowledged',
			type: 'checkbox',
			value: client?.faqAcknowledged || false,
			icon: CheckSquare,
			placeholder: 'Client has acknowledged reading the FAQ',
		},
		{
			key: 'preferredMeetingTimes',
			label: 'Meeting Availability',
			type: 'textarea',
			value: client?.preferredMeetingTimes || '',
			icon: Clock,
			placeholder: 'e.g., Weekdays 9-11 AM PST, avoid Fridays after 2 PM',
		},
		{
			key: 'additionalNotes',
			label: 'Other Notes',
			type: 'textarea',
			value: client?.additionalNotes || '',
			icon: MessageCircle,
			placeholder:
				'Any additional information, preferences, or requirements...',
		},
	];

	const handleEdit = (fieldKey) => {
		setEditValues({
			...editValues,
			[fieldKey]: fields.find((f) => f.key === fieldKey)?.value || '',
		});
		toggleEditMode(fieldKey);
	};

	const handleSave = async (fieldKey) => {
		const success = await updateField(fieldKey, editValues[fieldKey]);
		if (success) {
			setEditValues({ ...editValues, [fieldKey]: '' });
		}
	};

	const handleCancel = (fieldKey) => {
		setEditValues({ ...editValues, [fieldKey]: '' });
		cancelEdit(fieldKey);
	};

	const renderField = (field) => {
		const isEditing = isEditMode[field.key];
		const editValue = editValues[field.key] ?? field.value;
		const hasValue =
			field.type === 'checkbox'
				? field.value
				: field.value && field.value.trim() !== '';

		// Conditional styling: white background for filled fields, gray for unfilled
		const cardBgClass = hasValue ? 'bg-white shadow-sm' : 'bg-stone-50/50';

		return (
			<div
				key={field.key}
				className={`${cardBgClass} border border-gray-100 rounded-lg p-3 sm:p-4 transition-all duration-200 hover:border-primary/30`}>
				<div className='flex items-start justify-between'>
					<div className='flex-1'>
						<label className='flex items-center text-xs font-montserrat-medium uppercase tracking-wide text-gray-400 mb-1.5'>
							{field.icon && <field.icon className='w-3.5 h-3.5 mr-1.5 text-primary/30' />}
							{field.label}
						</label>

						{isEditing ? (
							<div className='space-y-3'>
								{field.type === 'checkbox' ? (
									<label className='flex items-center space-x-2'>
										<input
											type='checkbox'
											checked={editValue}
											onChange={(e) =>
												setEditValues({
													...editValues,
													[field.key]: e.target.checked,
												})
											}
											className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2'
										/>
										<span className='text-sm text-gray-700'>
											{field.placeholder}
										</span>
									</label>
								) : field.type === 'textarea' ? (
									<Textarea
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className='w-full'
										rows={4}
									/>
								) : (
									<Input
										type={field.type}
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className='w-full'
									/>
								)}

								<div className='flex items-center space-x-2'>
									<Button size='sm' onClick={() => handleSave(field.key)}>
										<Check className='w-4 h-4 mr-1' />
										Save
									</Button>
									<Button
										size='sm'
										variant='outline'
										onClick={() => handleCancel(field.key)}>
										<X className='w-4 h-4 mr-1' />
										Cancel
									</Button>
								</div>
							</div>
						) : (
							<div className='text-sm font-montserrat-medium text-gray-900'>
								{field.type === 'checkbox' ? (
									<span
										className={
											field.value ? 'text-green-600' : 'text-gray-400'
										}>
										{field.value ? 'Acknowledged' : 'Not acknowledged'}
									</span>
								) : field.value ? (
									field.type === 'textarea' ? (
										<div className='whitespace-pre-wrap'>{field.value}</div>
									) : (
										field.value
									)
								) : (
									<span className='text-gray-300 italic font-montserrat-regular'>
										Not provided
									</span>
								)}
							</div>
						)}
					</div>

					{!isEditing && (
						<Button
							size='sm'
							variant='ghost'
							onClick={() => handleEdit(field.key)}
							aria-label={`Edit ${field.label}`}
							className='ml-2 text-gray-300 hover:text-primary flex-shrink-0 transition-colors duration-200'>
							<Edit3 className='w-4 h-4' />
						</Button>
					)}
				</div>
			</div>
		);
	};

	return (
		<Card className='border-l-2 border-l-primary/20'>
			<Card.Header className='border-b border-gray-100'>
				<Card.Title className='flex items-center text-sm sm:text-base font-glacial uppercase tracking-wider text-gray-900'>
					<Calendar className='w-4 h-4 mr-2 text-primary' />
					Scheduling & Acknowledgment
				</Card.Title>
			</Card.Header>
			<Card.Content>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{fields.map(renderField)}
				</div>
			</Card.Content>
		</Card>
	);
};

export default SchedulingCard;
